import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PLANS } from '../lib/plans';
import useCheckout from '../hooks/useCheckout';

const LandingPage = () => {
  const { currentUser } = useAuth();
  const { startCheckout, loading: checkoutLoading } = useCheckout();
  const [selectedPlan, setSelectedPlan] = React.useState('annual');
  const [openFaq, setOpenFaq] = React.useState(null);
  const [currentAudio, setCurrentAudio] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const audioRef = React.useRef(null);

  const faqData = [
    {
      question: "How often do you release new content?",
      answer: "We publish new research summaries every week, typically 3-4 articles with audio summaries. Our team curates the latest and most impactful research across various fields."
    },
    {
      question: "What topics do you cover?",
      answer: "We cover AI & Technology, Healthcare & Medicine, Climate Science, Psychology, Business & Economics, and emerging scientific breakthroughs. Our content spans both academic research and industry developments."
    },
    {
      question: "Why is it so cheap?",
      answer: "We keep costs lean by focusing purely on research summaries — no app, no bloat. Early subscribers lock in the current price permanently. We may adjust pricing for new subscribers later, but existing members keep their rate."
    },
    {
      question: "How long are the audio summaries?",
      answer: "All audio summaries are under 10 minutes, typically 6-9 minutes. They're designed to be consumed during a coffee break or short commute while maintaining all key insights."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time. You'll continue to have access until your current billing period ends. No hidden fees or cancellation charges."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 30-day money-back guarantee. If you're not satisfied within the first 30 days, contact us for a full refund."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const STORAGE = 'https://firebasestorage.googleapis.com/v0/b/journalcoffee.firebasestorage.app/o';
  const freePreviewArticles = [
    {
      id: 1,
      title: "Attention Is All You Need",
      summary: "The paper that started the AI revolution — introducing the Transformer architecture that powers GPT, Gemini, and every modern language model.",
      audioUrl: `${STORAGE}/preview%2F1706.03762.mp3?alt=media`,
      duration: "~5 min"
    },
    {
      id: 2,
      title: "Deep Residual Learning for Image Recognition",
      summary: "How a simple skip-connection trick let researchers train 152-layer networks and win every major computer vision benchmark in 2015.",
      audioUrl: `${STORAGE}/preview%2F1512.03385.mp3?alt=media`,
      duration: "~5 min"
    },
    {
      id: 3,
      title: "Language Models are Few-Shot Learners",
      summary: "GPT-3 arrives with 175 billion parameters and shows that scale alone can unlock emergent abilities — changing what we expect from AI forever.",
      audioUrl: `${STORAGE}/preview%2F2005.14165.mp3?alt=media`,
      duration: "~5 min"
    }
  ];

  const scrollToPreview = () => {
    document.getElementById('preview-section').scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = () => {
    if (currentUser) {
      startCheckout(selectedPlan);
    } else {
      window.location.href = '/auth';
    }
  };

  const playAudio = (article) => {
    setCurrentAudio(article);
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z"/>
                <circle cx="14" cy="4" r="1"/>
              </svg>
            </div>
            <div className="text-2xl font-semibold text-gray-900">Journal Coffee</div>
          </div>
          <div className="flex items-center gap-4">
            {currentUser ? (
              <a href="/dashboard" className="bg-black text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                Dashboard
              </a>
            ) : (
              <a href="/auth" className="bg-black text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                Sign in
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-32 px-6 bg-gradient-to-br from-gray-50 via-white to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
            Science meets
            <span className="block text-indigo-600">your coffee break</span>
          </h1>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Stay current with cutting-edge research through digestible articles and 
            <strong> podcast-style summaries</strong> under 10 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleSubscribe}
              disabled={checkoutLoading}
              className="stripe-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {checkoutLoading ? 'Redirecting…' : 'Start from $6/month'}
            </button>
            <button
              onClick={scrollToPreview}
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-gray-400 transition-colors"
            >
              Listen to previews
            </button>
          </div>
        </div>
      </section>

      {/* Free Preview Section */}
      <section id="preview-section" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured research</h2>
            <p className="text-xl text-gray-600">Listen before you subscribe</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {freePreviewArticles.map((article) => (
              <div key={article.id} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{article.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{article.summary}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.369 4.369 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"/>
                    </svg>
                    {article.duration}
                  </div>
                  <button 
                    onClick={() => playAudio(article)}
                    className="text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
                  >
                    Play →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Cheaper than your coffee</h2>
            <p className="text-xl text-gray-600">Pick a plan. Cancel any time.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
            {PLANS.map((plan) => {
              const isAnnual = plan.id === 'annual';
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-8 border-2 cursor-pointer transition-all ${
                    isAnnual
                      ? 'border-indigo-500 shadow-lg shadow-indigo-100 bg-white'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  {isAnnual && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 stripe-gradient text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      Best value
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.label}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">{plan.billedAs}</p>
                  <ul className="space-y-3 mb-8">
                    {['All premium papers', 'Audio summaries', 'PDF downloads', 'Weekly new content', '30-day guarantee'].map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-gray-700">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan.id); handleSubscribe(); }}
                    disabled={checkoutLoading && selectedPlan === plan.id}
                    className={`w-full py-3.5 rounded-xl font-semibold text-base transition-opacity disabled:opacity-50 ${
                      isAnnual
                        ? 'stripe-gradient text-white hover:opacity-90'
                        : 'border-2 border-gray-900 text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {checkoutLoading && selectedPlan === plan.id ? 'Redirecting…' : `Get started`}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500">
            Both plans include everything. Annual saves you {Math.round((1 - 50 / 72) * 100)}% vs monthly.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about Journal Coffee</p>
          </div>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audio Player */}
      {currentAudio && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 truncate">{currentAudio.title}</h4>
                <p className="text-sm text-gray-600 truncate">{currentAudio.duration}</p>
              </div>
              
              {/* Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlayPause}
                  className="w-10 h-10 stripe-gradient rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                >
                  {isPlaying ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Progress Bar */}
              <div className="flex-1 max-w-md">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatTime(currentTime)}</span>
                  <div 
                    className="flex-1 h-1 bg-gray-200 rounded-full cursor-pointer"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full stripe-gradient rounded-full transition-all"
                      style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                    />
                  </div>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setCurrentAudio(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={currentAudio.audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={() => setIsPlaying(false)}
            autoPlay
          />
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z"/>
                <circle cx="14" cy="4" r="1"/>
              </svg>
            </div>
            <div className="text-2xl font-semibold text-gray-900">Journal Coffee</div>
          </div>
          <p className="text-gray-600 mb-4">Simplifying research, one summary at a time.</p>
          <div className="flex justify-center gap-6 mb-4">
            <a href="/terms" className="text-gray-500 hover:text-gray-700 transition-colors">Terms of Service</a>
            <a href="/privacy" className="text-gray-500 hover:text-gray-700 transition-colors">Privacy Policy</a>
          </div>
          <p className="text-sm text-gray-500">© 2024 Journal Coffee. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
